import { RateBasicWithValue } from '~hooks/useRates/models';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';

/**
 * Resolves where each party's rate came from and groups the parties that share a source.
 *
 * `JobService::SetMatchingRateOnAssignment` resolves a rate down four rungs, and only the
 * first two stamp a rate card:
 *
 *   1. the order's default (starred) card — taken directly, which SKIPS condition matching
 *      entirely, so a default can win over a more specific card that would have matched
 *   2. the most specific matching card (order-scoped first, then project-scoped)
 *   3. the order's own rate — stamped with `site_rate_schedule_id: nil`
 *   4. a named Rate record — also stamped with `site_rate_schedule_id: nil`
 *
 * The stamp records WHICH card, never WHY it was chosen, so rungs 1 and 2 are
 * indistinguishable from the stamp alone. Comparing the stamped card against the order's
 * current default separates them — see `defaultRateCardId` for the limits of that.
 *
 * Rungs 3 and 4 leave no card at all, which is why a job can show a live rate and no card.
 * Rung 4 is still recoverable because the assignment keeps the named Rate; rung 3 is not
 * distinguishable from a hand-entered rate on the wire, so it is reported as no source
 * rather than guessed at.
 */

// Customer first, then vendor, then driver — the order the rates are listed in on the job
// row, so the provenance blocks read in the same order as the cell.
const PROVENANCE_PARTY_ORDER = ['customer', 'vendor', 'driver'] as const;

type ProvenanceParty = (typeof PROVENANCE_PARTY_ORDER)[number];

type RateProvenanceKind = 'defaultCard' | 'matchedCard' | 'namedRate';

interface PartyRateSource {
  /** The card stamped on this party, when the rate came from one (rungs 1–2). */
  rateCard: SiteRateSchedule | null;
  /** The named Rate backing this party's rate. Only meaningful when no card is stamped. */
  rate: RateBasicWithValue | null;
}

export type RateSourcesByParty = Record<ProvenanceParty, PartyRateSource>;

interface BuildRateProvenanceGroupsArgs {
  /**
   * The order's default card, used only to tell rung 1 from rung 2.
   *
   * This is read-time inference against the CURRENT default: if the default is changed after
   * a job is stamped, a previously matched card can start reading as the default, or vice
   * versa. Same staleness family as the stamp itself, which is why the labels describe what
   * the card is rather than asserting why it was applied.
   */
  defaultRateCardId: string | null;
  sources: RateSourcesByParty;
}

export function buildRateProvenanceGroups({
  defaultRateCardId,
  sources,
}: BuildRateProvenanceGroupsArgs) {
  const groups: Array<{
    key: string;
    kind: RateProvenanceKind;
    rateCard: SiteRateSchedule | null;
    rate: RateBasicWithValue | null;
    parties: ProvenanceParty[];
  }> = [];

  PROVENANCE_PARTY_ORDER.forEach((party) => {
    const { rateCard, rate } = sources[party];

    let key: string;
    let kind: RateProvenanceKind;

    if (rateCard) {
      key = `card:${rateCard.id}`;
      kind = rateCard.id === defaultRateCardId ? 'defaultCard' : 'matchedCard';
    } else if (rate) {
      key = `rate:${rate.id}`;
      kind = 'namedRate';
    } else {
      return;
    }

    const groupForSameSource = groups.find((group) => group.key === key);

    if (groupForSameSource) {
      groupForSameSource.parties.push(party);

      return;
    }

    groups.push({
      key,
      kind,
      rateCard,
      // A stamped card IS the provenance, so the party's Rate record is not also reported.
      rate: rateCard ? null : rate,
      parties: [party],
    });
  });

  return groups;
}
